import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GenericForm, type GenericFormField } from '@/styles/GenericForm';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { GenericTag, type StatusType } from '@/styles/GenericTag';
import { GenericAttachmentsSection, type GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, type CommentAudienceOption } from '@/styles/GenericCommentsSection';
import { StandardButton, CancelButton } from '@/styles/StandardButtons';
import { LinkedDocsSection, type LinkedDoc } from '@/components/shared/LinkedDocsSection';
import { Label } from '@/components/ui/label';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useEntityComments } from '@/hooks/useEntityComments';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { useCostCodes, operationalCodes, costCodeLabel } from '@/lib/cost-codes';
import { fetchEntityAttachments, uploadEntityAttachments, deleteAttachment } from '@/lib/entity-attachments';
import type { TeamMemberLookup } from '@/lib/entity-api';
import {
  bidPackageApi, nextPackageStatuses, PACKAGE_STATUS_LABEL, INVITATION_STATUS_LABEL,
  type BidPackageRecord, type PackageInvitation, type PackageBid, type InvitationStatus,
} from './api';

const INVITATION_TAG: Record<InvitationStatus, StatusType> = { invited: 'pending', responded: 'completed' };

// The usual comment sections. Only the company writes on a package: the Team section is its own, the Vendor section is read by the vendors assigned to the package.
// (A client has no access to packages, so there is no Client section.)
const COMPANY_AUDIENCES: CommentAudienceOption[] = [{ value: 'internal', label: 'Team' }, { value: 'vendor', label: 'Vendor' }];
const VENDOR_AUDIENCES: CommentAudienceOption[] = [{ value: 'vendor', label: 'Vendor' }];

const money = (amount: number | null | undefined) => (amount == null ? '-' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount)));

interface BidPackageFormProps {
  /** null = a new package */
  pkg: BidPackageRecord | null;
  projects: { id: string; name: string }[];
  vendors: TeamMemberLookup[];
  /** A vendor looking at a package it was invited to: everything is read-only, with Create Bid / Decline instead of Save. */
  vendorView?: boolean;
  onClose: () => void;
  /** The package was saved (the list refreshes). */
  onSaved: () => void;
  /** vendorView: open the Bid form for this package. */
  onCreateBid?: (pkg: BidPackageRecord, invitation: PackageInvitation) => void;
  /** vendorView: open the vendor's existing bid. */
  onOpenBid?: (bidId: string) => void;
}

/**
 * The Bid Package form: ONE generic form (GenericForm) with the package's fields, the vendors it is assigned to (a generic multi-select, with the membership popup the other
 * forms use), the Invited Vendors list and the Linked Documents (company users only), files and the usual comment sections. An assigned vendor opens the same form read-only:
 * the package, its files and the Vendor comments, with Create Bid -- never the other vendors or the bids.
 */
export function BidPackageForm({ pkg, projects, vendors, vendorView = false, onClose, onSaved, onCreateBid, onOpenBid }: BidPackageFormProps) {
  const { companyId, user } = useUser();
  const { selectedProject } = useProject();
  const { codes } = useCostCodes();
  const editing = !!pkg;
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [invitations, setInvitations] = useState<PackageInvitation[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const createdIdRef = useRef<string | null>(null);

  const reloadInvitations = useCallback(async () => {
    if (!pkg?.id) return;
    try { setInvitations(await bidPackageApi.invitations(pkg.id)); } catch (e) { console.error('Could not load the invitations', e); }
  }, [pkg?.id]);
  useEffect(() => { void reloadInvitations(); }, [reloadInvitations]);

  // the bids of the package (Linked Documents): company users only, and they include the bids of vendors whose assignment was revoked
  const [packageBids, setPackageBids] = useState<PackageBid[]>([]);
  useEffect(() => {
    if (!pkg?.id || vendorView) { setPackageBids([]); return; }
    let cancelled = false;
    bidPackageApi.bids(pkg.id).then((list) => { if (!cancelled) setPackageBids(list); }).catch((e) => console.error('Could not load the bids of the package', e));
    return () => { cancelled = true; };
  }, [pkg?.id, vendorView]);

  // --- files (the generic staging: picked before the package exists, uploaded right away once it does) -------------------------------------------------------
  const uploadFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadEntityAttachments('bid_package', id, files, companyId, user.id);
  }, [companyId, user?.id]);
  const deleteFn = useCallback((id: string) => deleteAttachment(id, user?.id), [user?.id]);
  const { pendingFiles, uploadedFiles, setUploadedFiles, handleUpload, handleRemovePending, handleDelete, uploadPending } = useAttachmentStaging<GenericAttachment>({
    entityId: pkg?.id, uploadFn, deleteFn, newFilesPosition: 'prepend', onError: setError,
  });
  useEffect(() => {
    if (!pkg?.id) { setUploadedFiles([]); return; }
    let cancelled = false;
    fetchEntityAttachments('bid_package', pkg.id, { ascending: false }).then((list) => { if (!cancelled) setUploadedFiles(list); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [pkg?.id, setUploadedFiles]);

  const { comments, addComment, deleteComment } = useEntityComments('bid_package', pkg?.id);

  // --- fields ----------------------------------------------------------------------------------------------------------------------------------------------
  const costCodeOptions = useMemo(() => operationalCodes(codes, pkg?.cost_code_ids || []).map((c) => ({ label: costCodeLabel(c), value: c.id })), [codes, pkg?.cost_code_ids]);
  const wantedCodes: string[] = Array.isArray(formData.cost_code_ids) ? formData.cost_code_ids : [];
  const vendorOptions = useMemo(() => {
    const matches = (v: TeamMemberLookup) => (v.cost_code_ids || []).some((id) => wantedCodes.includes(id));
    return [...vendors]
      .sort((a, b) => Number(matches(b)) - Number(matches(a)))
      .map((v) => ({
        value: v.id,
        label: v.company_name || v.full_name || v.email,
        description: matches(v) ? 'Matches the cost codes' : undefined,
        attributes: { cost_code: v.cost_code_ids || [] },
      }));
  }, [vendors, wantedCodes]);

  const vendorFilters = useMemo(() => [
    { key: 'cost_code', label: 'Cost code', options: costCodeOptions },
  ], [costCodeOptions]);

  const statusOptions = nextPackageStatuses(pkg?.status ?? null).map((s) => ({ label: PACKAGE_STATUS_LABEL[s], value: s }));

  const fields = useMemo<GenericFormField[]>(() => ([
    { name: 'title', label: 'Title', type: 'text', required: true, row: 1, placeholder: 'Enter the package title' },
    { name: 'project_id', label: 'Project', type: 'select', required: true, row: 1, disabled: editing, options: projects.map((p) => ({ label: p.name, value: p.id })) },
    { name: 'due_date', label: 'Due Date', type: 'date', required: true, row: 2 },
    { name: 'status', label: 'Status', type: 'select', required: true, hideRequiredMark: true, row: 2, options: statusOptions },
    { name: 'cost_code_ids', label: 'Cost Codes', type: 'multiselect', row: 3, colSpan: 2, placeholder: 'Search cost codes', options: costCodeOptions },
    { name: 'description', label: 'Scope of Work', type: 'textarea', row: 4, colSpan: 2, placeholder: 'Describe the scope of work' },
    // who the package is assigned to: a vendor never sees this (nor any other vendor)
    ...(vendorView ? [] : [{ name: 'vendor_ids', label: 'Assign to Vendors', type: 'multiselect', row: 5, colSpan: 2, placeholder: 'Search vendors', options: vendorOptions, filters: vendorFilters, membershipProjectField: 'project_id' }]),
  ].map((f) => ({ ...f, disabled: f.disabled || vendorView })) as GenericFormField[]), [editing, projects, statusOptions, costCodeOptions, vendorOptions, vendorFilters, vendorView]);

  const initialValues = useMemo(() => ({
    title: pkg?.title || '',
    project_id: pkg?.project_id || getDefaultProjectIdForNewRecord(selectedProject),
    cost_code_ids: pkg?.cost_code_ids || [],
    description: pkg?.description || '',
    due_date: pkg?.due_date || '',
    status: pkg?.status || 'draft',
    vendor_ids: [] as string[],
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [pkg?.id]);
  const [seeded, setSeeded] = useState<Record<string, any>>(initialValues);
  useEffect(() => {
    // the assigned vendors are the invitation rows (loaded after the form opens)
    setSeeded({ ...initialValues, vendor_ids: invitations.map((i) => i.vendor_id) });
  }, [initialValues, invitations]);

  // --- save ------------------------------------------------------------------------------------------------------------------------------------------------
  const handleSubmit = async (data: Record<string, any>) => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: String(data.title || '').trim(),
        cost_code_ids: data.cost_code_ids || [],
        description: data.description || null,
        due_date: data.due_date,
        status: data.status,
        vendor_ids: data.vendor_ids || [],
      };
      const existingId = pkg?.id || createdIdRef.current;
      let id = existingId;
      if (existingId) {
        await bidPackageApi.update(existingId, payload);
      } else {
        const created = await bidPackageApi.create({ ...payload, project_id: data.project_id });
        id = created.id;
        createdIdRef.current = id; // a failed upload below never makes the next Save create a second package
      }
      if (id) await uploadPending(id);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the bid package');
    } finally {
      setSaving(false);
    }
  };

  // --- sections ----------------------------------------------------------------------------------------------------------------------------------------------
  const invitationColumns: GenericTableColumn<PackageInvitation>[] = [
    { key: 'vendor_name', header: 'Vendor' },
    { key: 'status', header: 'Status', width: '7rem', render: (row) => <GenericTag type="status" value={INVITATION_TAG[row.status]}>{INVITATION_STATUS_LABEL[row.status]}</GenericTag> },
    { key: 'bid_amount', header: 'Bid Amount', width: '8rem', align: 'right', render: (row) => money(row.bid_amount) },
  ];

  const linkedBids: LinkedDoc[] = packageBids.map((b) => ({ id: b.id, type: 'Bid', label: b.vendor_name, detail: money(b.amount), entityType: 'bid' }));
  const myInvitation = vendorView ? invitations[0] : undefined;

  const sections = (
    <div className="space-y-6">
      {editing && !vendorView && (
        <div className="space-y-2">
          <Label className="font-form-label">Invited Vendors</Label>
          <GenericTable
            columns={invitationColumns}
            data={invitations}
            selectable={false}
            rowKey={(row) => row.id}
            emptyState={<span className="text-sm text-gray-500">No vendors invited yet</span>}
            minTableWidth={0}
          />
        </div>
      )}
      {editing && !vendorView && <LinkedDocsSection docs={linkedBids} emptyText="No bids received yet" testId="package-linked-docs" />}
      <GenericAttachmentsSection
        attachments={uploadedFiles}
        pendingFiles={pendingFiles}
        onUpload={handleUpload}
        onDelete={editing && !vendorView ? handleDelete : undefined}
        onRemovePending={handleRemovePending}
        projectId={(formData.project_id || pkg?.project_id) ?? ''}
        mode={editing ? 'edit' : 'create'}
        disabled={vendorView}
      />
      <GenericCommentsSection
        comments={comments}
        onAddComment={async (content, audience) => { await addComment(content, audience || 'internal'); }}
        onDeleteComment={vendorView ? undefined : async (commentId) => { if (window.confirm('Are you sure you want to delete this comment?')) await deleteComment(commentId); }}
        mode={editing ? 'edit' : 'create'}
        audiences={vendorView ? VENDOR_AUDIENCES : COMPANY_AUDIENCES}
        disabled={vendorView}
        placeholder={editing ? 'Add a comment...' : 'Comments are available after saving the package'}
      />
    </div>
  );

  const vendorFooter = vendorView && pkg ? (
    <>
      <CancelButton onClick={onClose} />
      {myInvitation && myInvitation.bid_id && (
        <StandardButton type="button" customVariant="white" onClick={() => onOpenBid?.(myInvitation.bid_id as string)}>Open My Bid</StandardButton>
      )}
      {myInvitation && !myInvitation.bid_id && pkg.status === 'open' && (
        <StandardButton type="button" onClick={() => onCreateBid?.(pkg, myInvitation)}>Create Bid</StandardButton>
      )}
    </>
  ) : undefined;

  return (
    <GenericForm
      title="Bid Package"
      mode={editing ? 'edit' : 'create'}
      fields={fields}
      initialValues={seeded}
      onSubmit={handleSubmit}
      onCancel={onClose}
      onFieldChange={(_name, _value, data) => setFormData(data)}
      isOpen
      error={error}
      saving={saving}
      additionalSections={sections}
      customFooterButtons={vendorFooter}
    />
  );
}
